/**
 * DeviationDetail — read-only detail; RCA / CAPA from context (PRD Deviation UI v1).
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle, FileSearch, CheckCircle } from 'lucide-react';
import { deviationService } from '../../services/deviationService';
import RCAForm from '../rca/RCAForm';
import CAPAManager from '../qms/CAPAManager';
import './DeviationDetail.css';

const STATUS_BADGE = {
  open: 'badge-gray',
  under_investigation: 'badge-amber',
  resolved: 'badge-teal'
};

const STATUS_LABEL_KEY = {
  open: 'statusOpen',
  under_investigation: 'statusUnderInvestigation',
  resolved: 'statusResolved'
};

const SEVERITY_LABEL_KEY = {
  critical: 'severityCritical',
  major: 'severityMajor',
  minor: 'severityMinor'
};

const DeviationDetail = ({ organizationId, userId, userRole, deviationId, onClose }) => {
  const { t, i18n } = useTranslation('deviations');
  const [deviation, setDeviation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showRCAForm, setShowRCAForm] = useState(false);
  const [selectedRcaId, setSelectedRcaId] = useState(null);
  const [showCAPAForm, setShowCAPAForm] = useState(false);

  const canAct = ['management', 'ic_personnel'].includes(userRole);
  const hasRca = !!deviation?.rcaId;
  const capaCount = deviation?.correctiveActionIds?.length || 0;
  const hasCapa = capaCount > 0;

  const reload = () => {
    if (!deviationId) return;
    deviationService.getDeviation(deviationId).then(setDeviation);
  };

  useEffect(() => {
    if (!deviationId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    deviationService
      .getDeviation(deviationId)
      .then((dev) => {
        if (!cancelled) {
          setDeviation(dev);
          if (!dev) setError('notfound');
        }
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [deviationId]);

  const handleRCASaved = () => {
    setShowRCAForm(false);
    setSelectedRcaId(null);
    reload();
  };

  const openRCAForm = (rcaId) => {
    setSelectedRcaId(rcaId || null);
    setShowRCAForm(true);
  };

  const fmtTs = (ts) => {
    if (!ts) return t('dateUnknown');
    try {
      const d = ts.toDate ? ts.toDate() : new Date(ts);
      return d.toLocaleString(i18n.language === 'ro' ? 'ro-RO' : 'en-GB');
    } catch (_) {
      return t('dateUnknown');
    }
  };

  const locationDisplay = (dev) => {
    if (dev.locationPath) return dev.locationPath;
    if (dev.entityId) return `${dev.entityType || 'entity'}: ${dev.entityId}`;
    return t('unknownLocation');
  };

  const protocolDisplay = (dev) => {
    if (dev.protocolName) return dev.protocolName;
    if (dev.protocolId) return t('protocolFallback', { id: dev.protocolId });
    return '—';
  };

  if (loading) {
    return (
      <div className="deviation-detail-overlay">
        <div className="deviation-detail-container">
          <div className="deviation-detail-loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !deviation) {
    return (
      <div className="deviation-detail-overlay">
        <div className="deviation-detail-container">
          <div className="deviation-detail-header">
            <span>{t('errorLoading')}</span>
            <button type="button" className="btn-icon" onClick={onClose} aria-label={t('close')}>
              <X size={20} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tb = deviation.thresholdBreach;
  const legacyThreshold =
    !tb &&
    (deviation.expectedComplianceValue !== undefined || deviation.actualComplianceValue !== undefined);

  return (
    <div className="deviation-detail-overlay">
      <div className="deviation-detail-container">
        <div className="deviation-detail-header">
          <div className="detail-header-title">
            <span className="deviation-code-large">{deviation.deviation_code || deviation.id.slice(0, 12)}</span>
            <span className={`detail-severity-badge severity-${deviation.severity || 'unknown'}`}>
              {deviation.severity ? t(SEVERITY_LABEL_KEY[deviation.severity] || 'severityUnknown') : t('severityUnknown')}
            </span>
            <span className={`deviation-badge ${STATUS_BADGE[deviation.status] || 'badge-gray'}`}>
              {deviation.status ? t(STATUS_LABEL_KEY[deviation.status] || 'statusOpen') : t('statusOpen')}
            </span>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label={t('close')}>
            <X size={20} />
          </button>
        </div>

        <p className="detail-subtitle">
          {t('headerSubtitle')}
          {deviation.severity ? ` · ${t(SEVERITY_LABEL_KEY[deviation.severity] || 'severityUnknown')}` : ''}
        </p>

        <div className="deviation-detail-body">
          <div className="deviation-statement">
            <p className={`statement-text ${!deviation.deviationStatement ? 'statement-muted' : ''}`}>
              {deviation.deviationStatement ? `"${deviation.deviationStatement}"` : t('statementPlaceholder')}
            </p>
          </div>

          <div className="deviation-section">
            <h4>{t('sectionDetails')}</h4>
            <div className="detail-grid">
              <div className="detail-row">
                <span className="detail-label">{t('protocol')}</span>
                <span className="detail-value">{protocolDisplay(deviation)}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">{t('location')}</span>
                <span className="detail-value">{locationDisplay(deviation)}</span>
              </div>
              {(deviation.entityType || deviation.entityId) && (
                <div className="detail-row">
                  <span className="detail-label">{t('entity')}</span>
                  <span className="detail-value">
                    {deviation.entityType || '—'} — {deviation.entityId || '—'}
                  </span>
                </div>
              )}
              {deviation.assignedToName && (
                <div className="detail-row">
                  <span className="detail-label">{t('executor')}</span>
                  <span className="detail-value">{deviation.assignedToName}</span>
                </div>
              )}
              <div className="detail-row">
                <span className="detail-label">{t('executedAt')}</span>
                <span className="detail-value">{fmtTs(deviation.executedAt || deviation.createdAt)}</span>
              </div>
              {(deviation.riskName || deviation.riskId) && (
                <div className="detail-row">
                  <span className="detail-label">{t('risk')}</span>
                  <span className="detail-value">
                    {deviation.riskName || deviation.riskId}
                    {deviation.rpnAtTime > 0 && <span className="rpn-inline"> · RPN {deviation.rpnAtTime}</span>}
                  </span>
                </div>
              )}
              {deviation.biologicalEntities?.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">{t('biologicalEntities')}</span>
                  <span className="detail-value">{deviation.biologicalEntities.join(', ')}</span>
                </div>
              )}
              {deviation.standardRequirementIds?.length > 0 && (
                <div className="detail-row">
                  <span className="detail-label">{t('standards')}</span>
                  <span className="detail-value">{deviation.standardRequirementIds.join(', ')}</span>
                </div>
              )}
            </div>
          </div>

          {(tb || legacyThreshold) && (
            <div className="deviation-section threshold-section">
              <h4>{t('sectionThreshold')}</h4>
              {tb ? (
                <div className="threshold-block">
                  <div>
                    {t('thresholdType')}: {tb.thresholdType}
                  </div>
                  <div>
                    {t('thresholdExpected')}: {tb.expectedValue}%
                  </div>
                  <div>
                    {t('thresholdActual')}: {tb.actualValue}%
                  </div>
                  <div>
                    {t('thresholdGap')}: {tb.gap} {t('points')}
                  </div>
                </div>
              ) : (
                <div className="threshold-block">
                  <div>
                    {deviation.thresholdType || 'absolute'}
                  </div>
                  <div>
                    {t('thresholdExpected')}: {deviation.expectedComplianceValue ?? '—'}%
                  </div>
                  <div>
                    {t('thresholdActual')}: {deviation.actualComplianceValue ?? '—'}%
                  </div>
                  <div>
                    {t('thresholdGap')}: {deviation.gapValue ?? '—'} {t('points')}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="deviation-section">
            <h4>{t('sectionFailedSteps')}</h4>
            {!deviation.failedSteps?.length ? (
              <p className="muted-text">{t('noFailedSteps')}</p>
            ) : (
              deviation.failedSteps.map((step, si) => (
                <div key={si} className="failed-step">
                  <span className="step-name">{step.stepName || step.stepId || `Step ${si + 1}`}</span>
                  {(step.failedActions || []).map((action, ai) => (
                    <div key={ai} className="failed-action">
                      <X size={12} className="failed-icon" />
                      {action.actionName || action.failureType || '—'}
                    </div>
                  ))}
                </div>
              ))
            )}
          </div>

          {deviation.isRecurring && (
            <div className="deviation-section recurring-section">
              <AlertTriangle size={16} />
              <div>
                <strong>{t('recurringTitle')}</strong> — {deviation.occurrenceCount ?? '—'} {t('occurrences')}
                {deviation.firstOccurredAt && (
                  <span className="first-occurrence">
                    {' '}
                    ({t('firstOccurrence')}: {fmtTs(deviation.firstOccurredAt)})
                  </span>
                )}
              </div>
            </div>
          )}

          <div className="deviation-section">
            <h4>{t('sectionActions')}</h4>

            {hasRca && (
              <div className="action-link-row">
                <FileSearch size={16} />
                <span>
                  {t('rcaLinked')}: {deviation.rca_code || deviation.rcaId}
                </span>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => openRCAForm(deviation.rcaId)}>
                  {t('viewRCA')} →
                </button>
              </div>
            )}

            {hasCapa && (
              <div className="action-link-row">
                <CheckCircle size={16} />
                <span>
                  {t('capaLinked')}: {capaCount} {t('capaCountLabel')}
                </span>
              </div>
            )}

            {!hasRca && canAct && !showRCAForm && !showCAPAForm && (
              <button type="button" className="btn btn-secondary" onClick={() => openRCAForm(null)}>
                {t('createRCA')}
              </button>
            )}

            {!hasRca && canAct && !hasCapa && !showCAPAForm && !showRCAForm && (
              <button type="button" className="btn btn-secondary" onClick={() => setShowCAPAForm(true)}>
                {t('createCAPADirect')}
              </button>
            )}
          </div>

          {showRCAForm && (
            <RCAForm
              organizationId={organizationId}
              userId={userId}
              userRole={userRole}
              rcaId={selectedRcaId || undefined}
              deviationId={!selectedRcaId ? deviationId : null}
              incidentId={null}
              onSave={handleRCASaved}
              onCancel={() => {
                setShowRCAForm(false);
                setSelectedRcaId(null);
              }}
            />
          )}

          {showCAPAForm && (
            <div className="capa-inline-wrap">
              <CAPAManager
                organizationId={organizationId}
                userId={userId}
                userRole={userRole}
                initialSourceType="deviation"
                initialDeviationId={deviationId}
                initialSourceRef={deviation.deviation_code || deviationId}
                initialRiskId={deviation.riskId || null}
                singleEntryMode
              />
              <button
                type="button"
                className="btn btn-secondary btn-sm capa-close-btn"
                onClick={() => {
                  setShowCAPAForm(false);
                  reload();
                }}
              >
                {t('close')}
              </button>
            </div>
          )}
        </div>

        <div className="deviation-detail-footer">
          <button type="button" className="btn btn-primary" onClick={onClose}>
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeviationDetail;
